import { Check } from "lucide-react";
import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  ListBoxProps as AriaListBoxProps,
  composeRenderProps,
  ListBoxItemProps,
} from "react-aria-components";
import { composeTailwindRenderProps } from "../../utils";

/**
 * ListBox
 *
 * Styled wrapper around `react-aria-components` ListBox and related parts
 * used in dropdowns and menus.
 */
interface ListBoxProps<T> extends Omit<AriaListBoxProps<T>, "layout" | "orientation"> {}

export function ListBox<T extends object>({ children, ...props }: ListBoxProps<T>) {
  return (
    <AriaListBox {...props} className={composeTailwindRenderProps(props.className, "outline-0")}>
      {children}
    </AriaListBox>
  );
}

export function getItemStyles({
  isSelected,
  isDisabled,
  size,
}: {
  isSelected?: boolean;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
}) {
  let styles = `outline-none group relative flex items-center gap-8 cursor-default select-none py-2 px-3 will-change-transform text-sm forced-color-adjust-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-action-default data-[focus-visible]:ring-inset`;

  if (isSelected) {
    styles += " bg-background-input text-text-primary forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]";
  } else {
    styles += " text-text-primary hover:bg-background-hover";
  }

  if (isDisabled) {
    styles += " text-text-disabled forced-colors:text-[GrayText]";
  }

  if (size === "sm") {
    styles += " py-1 text-xs";
  }

  return styles;
}

/**
 * ListBoxItem with Edges styling.
 */
export function ListBoxItem(props: ListBoxItemProps) {
  const textValue = props.textValue || (typeof props.children === "string" ? props.children : undefined);

  return (
    <AriaListBoxItem {...props} textValue={textValue} className={(props) => getItemStyles(props)}>
      {composeRenderProps(props.children, (children) => (
        <>
          {children}
          <div className="absolute bottom-0 left-4 right-4 hidden h-px bg-text-inverse/20 forced-colors:bg-[HighlightText] [.group[data-selected]:has(+[data-selected])_&]:block" />
        </>
      ))}
    </AriaListBoxItem>
  );
}

export function getDropdownItemStyles({
  isDisabled,
  isFocused,
  isOpen,
  isSelected,
}: {
  isDisabled?: boolean;
  isFocused?: boolean;
  isOpen?: boolean;
  isSelected?: boolean;
}) {
  let styles =
    "group flex items-center gap-4 cursor-default select-none py-2 pl-3 pr-1 outline-none text-sm forced-color-adjust-none hover:bg-background-hover dark:hover:bg-background-hover";

  if (isDisabled) {
    styles += " text-text-disabled forced-colors:text-[GrayText]";
  } else {
    styles += " text-text-primary";
  }

  if (isFocused) {
    styles += " bg-background-hover forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]";
  }

  if (isSelected) {
    styles += " bg-background-selected font-medium forced-colors:bg-[Highlight] forced-colors:text-[HighlightText]";
  }

  if (!isFocused && !isSelected && isOpen) {
    styles += " bg-background-input";
  }

  return styles;
}

/**
 * DropdownItem with selection checkmark and hover states.
 */
export function DropdownItem(props: ListBoxItemProps) {
  const textValue = props.textValue || (typeof props.children === "string" ? props.children : undefined);
  return (
    <AriaListBoxItem {...props} textValue={textValue} className={(props) => getDropdownItemStyles({ ...props })}>
      {composeRenderProps(props.children, (children, { isSelected }) => (
        <>
          <span className="flex flex-1 items-center gap-2 truncate font-normal group-selected:font-semibold">
            {children}
          </span>
          <span className="flex w-5 items-center">{isSelected && <Check className="h-4 w-4" />}</span>
        </>
      ))}
    </AriaListBoxItem>
  );
}
